/**
 * Pure helpers behind the full-pipeline wizard: DAG selection from the URL
 * and identity-tool resolution from the declared catalogue. No React here —
 * everything is unit-testable with plain fixtures.
 */
import type { DagInfo, PhaseGroup, ToolEntry } from "./api";
import { resolveText } from "./tooltext";

/**
 * Pick the wizard's DAG: an explicitly requested pipeline (by name) wins;
 * a single declared pipeline auto-selects; otherwise null — the wizard
 * renders its picker.
 */
export function pickDag(
  dags: DagInfo[],
  requested: string | null | undefined,
): DagInfo | null {
  if (requested) {
    const hit = dags.find((dag) => dag.name === requested);
    if (hit) return hit;
  }
  return dags.length === 1 ? dags[0] : null;
}

/** Display name of a pipeline: server-declared label, else its name. */
export function dagLabel(dag: DagInfo, lang: string): string {
  return resolveText(dag.label, lang) || dag.name;
}

/** The card's text in the operator's language; empty when the server declares none. */
export function dagDescription(dag: DagInfo, lang: string): string {
  return resolveText(dag.description, lang);
}

/**
 * The tool whose declared run params form the wizard's identity step —
 * matched by the tool's `name` field (NOT its key) against the DAG's
 * declared `identity_tool`. Null when the DAG declares no identity tool
 * (the params step is skipped) or when the declared name is not in the
 * catalogue (the caller must keep the launch gate shut).
 */
/**
 * The tool that receives the wizard's upload — the pipeline's FIRST step,
 * declared by the server as entry_tool. Falls back to the identity tool
 * for a backend that does not send it (single-step pipelines, where the two
 * are the same tool anyway).
 */
export function resolveEntryTool(
  dag: Pick<DagInfo, "entry_tool" | "identity_tool"> | null | undefined,
  groups: PhaseGroup[],
): ToolEntry | null {
  return resolveIdentityTool(dag?.entry_tool ?? dag?.identity_tool, groups);
}

export function resolveIdentityTool(
  identityToolName: string | null | undefined,
  groups: PhaseGroup[],
): ToolEntry | null {
  if (!identityToolName) return null;
  return (
    groups
      .flatMap((group) => group.tools)
      .find((tool) => tool.name === identityToolName) ?? null
  );
}
